<template>
  <section v-if="graphData.hasData" class="entity-graph">
    <SectionTitle title="知识图谱" />

    <div class="entity-graph__body">
      <div class="entity-graph__canvas-wrap">
        <div ref="chartRef" class="entity-graph__canvas" />
        <GraphCategoryLegend
          :nodes="graphData.nodes"
          :value="highlightedCategory"
          dark
          @input="handleCategoryFilter"
        />
      </div>
      <div v-if="loading" class="entity-graph__loading">加载中...</div>

      <aside v-if="selectedNode" class="entity-graph__panel">
        <h4 class="entity-graph__panel-name">{{ selectedNode.name }}</h4>
        <p v-if="selectedNode.identityName" class="entity-graph__panel-role">
          {{ selectedNode.identityName }}
        </p>

        <GraphAttributeList
          :fields="selectedAttributes"
          :entity-id="selectedNode && selectedNode.entityId"
          :loading="attributeLoading"
          :error="panelError"
        />
      </aside>
    </div>
  </section>
</template>

<script>
import SectionTitle from './SectionTitle.vue'
import { fetchKnowledgeEntityAttribute, mapEntityAttribute } from '@/api/knowledge'
import {
  createGraphVis,
  destroyGraphVis,
  buildGraphVisPayload,
  renderGraphVis,
  pickBusinessNode,
  bindNeighborHighlight,
  bindLinkInteractions,
  highlightGraphCategory,
  focusGraphNeighbors,
  clearGraphNeighborFocus,
  applyGraphNodeImages,
  fitGraphVis
} from '@/utils/graphVisHelper'
import GraphCategoryLegend from '@/components/graph/GraphCategoryLegend.vue'
import GraphAttributeList from '@/components/graph/GraphAttributeList.vue'

export default {
  name: 'EntityKnowledgeGraph',
  components: { SectionTitle, GraphCategoryLegend, GraphAttributeList },
  props: {
    graphData: {
      type: Object,
      default: () => ({ nodes: [], relations: [], focusEntityId: null, focusNodeId: null, hasData: false })
    },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      visgraph: null,
      eventsBound: false,
      selectedNode: null,
      selectedAttributes: [],
      attributeLoading: false,
      panelError: '',
      attributeRequestId: 0,
      graphRenderId: 0,
      highlightedCategory: ''
    }
  },
  watch: {
    graphData: {
      deep: true,
      handler() {
        this.selectFocusNode()
        this.$nextTick(() => this.renderGraph())
      }
    },
    loading(value) {
      if (!value) {
        this.$nextTick(() => this.renderGraph())
      }
    }
  },
  mounted() {
    this.selectFocusNode()
    this.$nextTick(() => {
      this.renderGraph()
    })
  },
  beforeDestroy() {
    destroyGraphVis(this.visgraph)
    this.visgraph = null
    this.eventsBound = false
  },
  methods: {
    findFocusNode() {
      const { focusNodeId, focusEntityId } = this.graphData
      if (focusNodeId) {
        const byNeo4jId = (this.graphData.nodes || []).find((node) => node.id === focusNodeId)
        if (byNeo4jId) {
          return byNeo4jId
        }
      }
      if (!focusEntityId) {
        return null
      }
      return (this.graphData.nodes || []).find((node) => node.entityId === focusEntityId) || null
    },
    selectFocusNode() {
      const focusNode = this.findFocusNode()
      if (focusNode) {
        this.handleNodeClick(focusNode)
      } else {
        this.selectedNode = null
        this.selectedAttributes = []
        this.panelError = ''
      }
    },
    async handleNodeClick(node) {
      this.selectedNode = node
      if (!node || !node.entityId) {
        this.selectedAttributes = []
        this.panelError = node ? '该节点暂无实体属性' : ''
        return
      }
      await this.loadNodeAttributes(node.entityId)
    },
    async loadNodeAttributes(entityId) {
      const requestId = ++this.attributeRequestId
      this.attributeLoading = true
      this.panelError = ''
      this.selectedAttributes = []
      try {
        const list = await fetchKnowledgeEntityAttribute(entityId)
        if (requestId !== this.attributeRequestId) {
          return
        }
        this.selectedAttributes = (list || []).map(mapEntityAttribute)
      } catch (error) {
        if (requestId !== this.attributeRequestId) {
          return
        }
        this.selectedAttributes = []
        this.panelError = '属性加载失败'
      } finally {
        if (requestId === this.attributeRequestId) {
          this.attributeLoading = false
        }
      }
    },
    ensureGraph() {
      if (this.visgraph) {
        return this.visgraph
      }
      const container = this.$refs.chartRef
      if (!container || !this.graphData.hasData) {
        return null
      }
      try {
        this.visgraph = createGraphVis(container, { dark: true })
        this.bindGraphEvents()
        return this.visgraph
      } catch (error) {
        console.error(error)
        this.$message.error(error.message || '图谱初始化失败')
        return null
      }
    },
    bindGraphEvents() {
      if (!this.visgraph || this.eventsBound) {
        return
      }
      this.eventsBound = true
      const that = this
      this.visgraph.registEventListener('node', 'click', (event, node) => {
        const business = pickBusinessNode(node)
        that.highlightedCategory = ''
        if (business && that.visgraph && String(that.visgraph._highlightNodeId) === String(business.id)) {
          that.clearNodeFocus()
          return
        }
        that.focusClickedNode(business)
        that.handleNodeClick(business)
      })
      this.visgraph.registEventListener('scene', 'whiteSpaceClick', () => {
        that.clearNodeFocus()
      })
      bindNeighborHighlight(this.visgraph)
      bindLinkInteractions(this.visgraph, {
        onClick() {
          that.highlightedCategory = ''
          that.selectedNode = null
          that.selectedAttributes = []
          that.panelError = ''
        }
      })
    },
    focusClickedNode(node) {
      if (!this.visgraph || !node || !node.id) return
      focusGraphNeighbors(this.visgraph, node.id)
    },
    clearNodeFocus() {
      clearGraphNeighborFocus(this.visgraph)
      this.selectedNode = null
      this.selectedAttributes = []
      this.panelError = ''
    },
    async renderGraph() {
      if (!this.graphData.hasData || this.loading) {
        return
      }
      const graph = this.ensureGraph()
      if (!graph) {
        return
      }
      const renderId = ++this.graphRenderId
      const payload = await buildGraphVisPayload(this.graphData, { dark: true, skipImages: true })
      if (renderId !== this.graphRenderId || !this.visgraph) {
        return
      }
      renderGraphVis(this.visgraph, payload, { charge: -280 })
      this.highlightedCategory = ''
      this.$nextTick(() => fitGraphVis(this.visgraph))
      applyGraphNodeImages(this.visgraph, this.graphData).catch(() => {})
    },
    handleCategoryFilter(category) {
      this.highlightedCategory = category || ''
      highlightGraphCategory(this.visgraph, this.highlightedCategory)
      fitGraphVis(this.visgraph)
    }
  }
}
</script>

<style lang="scss" scoped>
.entity-graph {
  margin-bottom: $spacing-3xl;

  &__body {
    position: relative;
    display: flex;
    gap: $spacing-base;
    min-height: 520px;
    padding: $spacing-base;
    background: $color-brown-dark;
    border-radius: 4px;
  }

  &__loading {
    position: absolute;
    inset: $spacing-base;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($color-brown-dark, 0.72);
    color: rgba($color-white, 0.85);
    font-size: rem(14);
    border-radius: 4px;
  }

  &__canvas-wrap {
    position: relative;
    flex: 1;
    min-width: 0;
  }

  &__canvas {
    width: 100%;
    height: 520px;
  }

  &__panel {
    position: relative;
    z-index: 2;
    width: 260px;
    flex-shrink: 0;
    padding: $spacing-base;
    background: rgba($color-white, 0.95);
    border-radius: 4px;
    align-self: stretch;
    max-height: 520px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__panel-name {
    flex-shrink: 0;
    font-family: $font-serif;
    font-size: rem(18);
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-xs;
  }

  &__panel-role {
    flex-shrink: 0;
    font-size: rem(12);
    color: $success-color;
    margin-bottom: $spacing-sm;
  }

  &__panel-loading,
  &__panel-empty,
  &__panel-meta {
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  &__panel-loading,
  &__panel-empty {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    font-size: rem(13);
    color: $color-text-light;
    line-height: 1.7;
  }

  &__panel-meta {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
  }

  &__panel-row {
    display: flex;
    font-size: rem(12);
    line-height: 1.7;

    dt {
      flex-shrink: 0;
      color: $color-text-light;
    }

    dd {
      color: $color-text;
      word-break: break-all;
    }
  }

  &__panel-media {
    dt {
      display: block;
      font-size: rem(12);
      color: $color-text-light;
      margin-bottom: $spacing-xs;
    }
  }

  &__panel-images {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: $spacing-xs;

    img {
      width: 100%;
      height: 72px;
      object-fit: cover;
      border-radius: 4px;
    }
  }
}

@media (max-width: 960px) {
  .entity-graph__body {
    flex-direction: column;
  }

  .entity-graph__panel {
    width: 100%;
    max-height: none;
  }
}
</style>
